import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Textarea } from "@/components/kit";
import { toast } from "sonner";
import { LogOut } from "lucide-react";

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function Shell({ children }) {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-warm-cream flex flex-col" data-testid="signin-gate">
      <div className="bg-ink text-cream px-4 py-3 flex items-center gap-3">
        <img src="/logo.png" alt="" className="w-9 h-9" />
        <div className="font-zilla font-semibold leading-none">Rosemoor</div>
        <button onClick={logout} data-testid="gate-logout" className="ml-auto text-cream/70 hover:text-cream text-[13px] flex items-center gap-1"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 flex items-start justify-center">
        <div className="w-full max-w-md mt-6 space-y-4 pb-12">{children}</div>
      </div>
    </div>
  );
}

export default function SignInGate({ gate, onResolved }) {
  const [paying, setPaying] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(!!gate.message_sent);
  const first = gate.name || "there";
  const amount = gate.amount_cents ? dollars(gate.amount_cents) : null;
  const cadenceLabel = gate.cadence === "monthly" ? "/month" : gate.cadence === "yearly" ? "/year" : "";

  const pay = async () => {
    setPaying(true);
    try {
      const r = await api.post("/membership/me/pay-dues", {});
      if (!r.data.paid) { toast.info(r.data.message); return; }
      toast.success("Payment complete — welcome back!"); await onResolved();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not process payment"); }
    finally { setPaying(false); }
  };
  const send = async () => {
    if (!msg.trim()) return toast.error("Please write a message.");
    setSending(true);
    try { await api.post("/membership/me/suspended-message", { message: msg }); toast.success("Message sent to the office"); setSent(true); setMsg(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send message"); }
    finally { setSending(false); }
  };

  const PaymentBlock = (
    <Card className="!p-6" data-testid="gate-payment">
      <h2 className="font-zilla text-2xl font-semibold mb-1">Settle your dues</h2>
      <p className="text-[15px] text-ink/70 mb-4">
        {amount ? `Your membership dues of ${amount}${cadenceLabel} are due.` : "Your membership needs to be renewed."}
      </p>
      <Button data-testid="gate-pay-btn" disabled={paying} onClick={pay} className="w-full justify-center">
        {paying ? "Processing…" : (amount ? `Pay ${amount}` : "Renew my membership")}
      </Button>
    </Card>
  );

  const MessageBlock = (
    <Card className="!p-6" data-testid="gate-message">
      <h2 className="font-zilla text-2xl font-semibold mb-1">Message the office</h2>
      {sent ? (
        <p className="text-[15px] text-ink/70" data-testid="gate-message-sent">Thanks — your message has been sent to the Rosemoor office. You can send another next time you sign in.</p>
      ) : (
        <>
          <p className="text-[15px] text-ink/70 mb-3">Send a note to the Rosemoor team. You can send one message per sign-in.</p>
          <Textarea data-testid="gate-message-input" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write your message…" />
          <Button data-testid="gate-message-send" disabled={sending} onClick={send} className="w-full justify-center mt-3">{sending ? "Sending…" : "Send message"}</Button>
        </>
      )}
    </Card>
  );

  if (gate.suspended) {
    const sv = gate.suspended_view || {};
    const showPay = !!sv.payment;
    const showMsg = !!sv.message;
    return (
      <Shell>
        <div className="text-center mb-1">
          <div className="w-14 h-14 rounded-full bg-maroon/15 text-maroon flex items-center justify-center mx-auto mb-3 font-zilla text-3xl font-semibold">!</div>
          <h1 className="font-zilla text-3xl font-semibold" data-testid="gate-suspended-title">Your account is suspended</h1>
          <p className="text-[15px] text-ink/70 mt-1">Hi {first}, your Rosemoor membership is currently suspended.</p>
          {gate.suspend_reason && <p className="text-[14px] text-ink/60 mt-1" data-testid="gate-suspend-reason">Reason: {gate.suspend_reason}</p>}
        </div>
        {showPay && PaymentBlock}
        {showMsg && MessageBlock}
        {!showPay && !showMsg && (
          <Card className="!p-6 text-center" data-testid="gate-neutral">
            <p className="text-[15px] text-ink/70">Please contact the Rosemoor office to resolve your account.</p>
          </Card>
        )}
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-1">
        <h1 className="font-zilla text-3xl font-semibold" data-testid="gate-dues-title">Welcome back, {first}</h1>
        <p className="text-[15px] text-ink/70 mt-1">Your membership dues need to be settled before you can continue.</p>
      </div>
      {typeof gate.days_left === "number" && (
        <div className="bg-gold/20 border border-gold/40 rounded-xl px-4 py-3 text-center" data-testid="gate-countdown">
          <span className="text-[15px] font-semibold text-ink">
            {gate.days_left === 0 ? "This is your last day to resolve this." : `You have ${gate.days_left} day${gate.days_left === 1 ? "" : "s"} left to resolve this.`}
          </span>
          <div className="text-[13px] text-ink/60 mt-0.5">After that, your account will be suspended.</div>
        </div>
      )}
      {PaymentBlock}
    </Shell>
  );
}
